import React, { useState, useEffect } from 'react';
import useTranslate from '../../Hooks/useTranslate';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  Collapse,
} from '@mui/material';
import {
  CenteredFlexBox,
  FullSizeCenteredFlexBox,
} from '../../Components/FlexBox';
import { getFullPath, useFieldAtom } from './useFieldValue';
import { useLookupOptions } from './useLookupOptions';
import { SchemaRenderer } from './SchemaRenderer';
import { BlockedField } from './binding/PointedFields';
import CollapseToggle from '../Button/CollapseToggle';

/** An option's value and label, from whichever of the two shapes the lookup answered with. */
const optionValue = (opt) => (opt.value !== undefined ? opt.value : opt.id);
const optionLabel = (opt) => (opt.label !== undefined ? opt.label : opt.name);

export const StaticArrayField = ({ basePath, field }) => {
  const path = getFullPath(basePath, field.key);
  const translate = useTranslate();

  const [items, setItems] = useFieldAtom(path);
  const [options, loading, waitingOn] = useLookupOptions(field, basePath, {
    value: items,
    onChange: setItems,
  });

  const [collapsed, setCollapsed] = useState(false);

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  useEffect(() => {
    if (options.length > 0) {
      let sourceData = [];
      if (Array.isArray(items) && items.length > 0) {
        sourceData = items;
      } else if (
        items &&
        typeof items === 'object' &&
        Object.keys(items).length > 0
      ) {
        sourceData = Object.values(items);
      } else if (
        Array.isArray(field.defaultValue) &&
        field.defaultValue.length > 0
      ) {
        sourceData = field.defaultValue;
      }

      const isStoreInSync =
        Array.isArray(items) &&
        items.length === options.length &&
        items.every(
          (item, index) =>
            item &&
            item.optionValue === optionValue(options[index]) &&
            item.optionLabel === optionLabel(options[index])
        );

      if (isStoreInSync) return;

      const defaults = Object.fromEntries(
        (field.itemSchema || []).map((subField) => [
          subField.key,
          subField.defaultValue ?? '',
        ])
      );

      const mergedItems = options.map((opt, index) => {
        const optVal = optionValue(opt);

        const existingData =
          sourceData.find((item) => item?.optionValue === optVal) ||
          sourceData[index] ||
          {};

        return {
          ...defaults,
          ...existingData,
          optionValue: optVal,
          optionLabel: optionLabel(opt),
        };
      });

      setItems(mergedItems, { isInit: true });
    }
  }, [options, items, field.itemSchema, field.defaultValue, setItems]);

  if (loading) {
    return (
      <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">
          {translate('LOADING')} {translate(field.label)}...
        </Typography>
      </Box>
    );
  }

  // A list whose options are not fetched yet has nothing to reconcile against, so it draws its
  // heading and no rows until the sibling it depends on is filled in.
  const held = Array.isArray(items)
    ? items
    : items && typeof items === 'object'
      ? Object.values(items)
      : [];
  const safeRenderItems = waitingOn.length > 0 ? [] : held;

  return (
    <BlockedField waitingOn={waitingOn}>
      <Paper variant="outlined" sx={{ p: '16px' }}>
        <CenteredFlexBox justifyContent="space-between">
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {translate(field.label)}
          </Typography>
          <CollapseToggle collapsed={collapsed} onToggle={toggleCollapsed} />
        </CenteredFlexBox>
        <Collapse in={!collapsed}>
          <Grid container columnSpacing={3} rowSpacing={1}>
            {safeRenderItems.map((item, index) => (
              <Grid key={index} size={6}>
                <FullSizeCenteredFlexBox
                  justifyContent="space-between"
                  gap="16px"
                >
                  {item?.optionLabel && (
                    <Typography
                      variant="body1"
                      sx={{
                        minWidth: '150px',
                        fontWeight: 'bold',
                        flexGrow: 1,
                      }}
                    >
                      {translate(item.optionLabel)}
                    </Typography>
                  )}

                  <SchemaRenderer
                    fields={field.itemSchema}
                    basePath={`${path}.${index}`}
                  />
                </FullSizeCenteredFlexBox>
              </Grid>
            ))}
          </Grid>
        </Collapse>
      </Paper>
    </BlockedField>
  );
};
